import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import QRCodeStyling from 'qr-code-styling';
import axios from '../utils/axios';
import { API_URL } from '../constants';

const qrCode = new QRCodeStyling({ width: 200, height: 200, type: 'svg' });

const EXPIRE_MS = 2 * 60 * 1000;
const REFRESH_AT_REMAINING_MS = 30 * 1000;

const ScanLoginWidget: FC = () => {
  const qrRef = useRef<HTMLDivElement>(null);
  const barcodeRef = useRef<SVGSVGElement>(null);
  const [url, setUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [remaining, setRemaining] = useState(EXPIRE_MS);
  const [showBarcode, setShowBarcode] = useState(false);
  const [error, setError] = useState('');

  const generate = useCallback(async () => {
    try {
      setError('');
      const res = await axios.post(`${API_URL}/auth/scan-login/generate`, {});
      if (!res) return; // 418 → cookie removed, page reloading
      const newUrl: string = res.data.url;
      setUrl(newUrl);
      setExpiresAt(new Date(res.data.expiresAt));
      setRemaining(EXPIRE_MS);
      qrCode.update({ data: newUrl });
    } catch {
      setError('Failed to generate login code. Please refresh.');
    }
  }, []);

  useEffect(() => {
    if (qrRef.current) {
      qrCode.append(qrRef.current);
    }
    generate();
  }, [generate]);

  useEffect(() => {
    if (!expiresAt) return;
    const interval = setInterval(() => {
      const rem = expiresAt.getTime() - Date.now();
      if (rem <= 0) {
        clearInterval(interval);
        setRemaining(0);
        generate();
      } else {
        setRemaining(rem);
        if (rem <= REFRESH_AT_REMAINING_MS) {
          clearInterval(interval);
          generate();
        }
      }
    }, 500);
    return () => clearInterval(interval);
  }, [expiresAt, generate]);

  useEffect(() => {
    if (!url || !barcodeRef.current) return;
    JsBarcode(barcodeRef.current, url, {
      format: 'CODE128',
      height: 60,
      margin: 4,
      fontSize: 10,
      displayValue: false,
    });
  }, [url, showBarcode]);

  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  const countdownLabel = `${minutes}:${String(seconds).padStart(2, '0')}`;

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ marginBottom: 12 }}>Log in on a scanner</h3>
      {error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
        <>
          <div ref={qrRef} style={{ display: 'inline-block' }} />
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>Expires in {countdownLabel}</span>
            <button type="button" onClick={generate}>
              Refresh
            </button>
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={() => setShowBarcode(v => !v)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              {showBarcode ? '\u25b4' : '\u25be'} Using a 1D scanner?
            </button>
            {showBarcode && (
              <div style={{ marginTop: 8 }}>
                <svg ref={barcodeRef} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ScanLoginWidget;
